import type {PropertyMap} from "../../common/utils/objects";
import {COLOR_GRAY, COLOR_HIGHLIGHT, COLOR_PRIMARY, COLOR_SECONDARY, COLOR_WHITE} from "./colors";
import {customElement, property, query} from "lit/decorators.js";
import {UnLitElement} from "../../common/elements";
import {GoogleMapsFactory} from "./googleMapsFactory";
import {resolve} from "../../container";
import {elementFrom} from "../../common/utils/html";
import {BOOTSTRAP} from "../../common/resolutionConstants";
import {Resolution} from "../../common/resolution";

const MARKER_COLOR_PROPS: PropertyMap<string> = {
    primary: COLOR_PRIMARY,
    secondary: COLOR_SECONDARY,
    gray: COLOR_GRAY
};

@customElement("eop-google-maps-poi")
export class EopGoogleMapsPoi extends UnLitElement {
    @property({attribute: "visual-data-title"})
    public title: string;
    @property({attribute: "color"})
    private color: string;
    @property({attribute: "lat", type: Number})
    private lat: number;
    @property({attribute: "lng", type: Number})
    private lng: number;
    @query(".close-button")
    private closeButton: HTMLElement;

    public marker: google.maps.marker.AdvancedMarkerElement;

    public constructor(
        private googleMapsFactory: GoogleMapsFactory = resolve(GoogleMapsFactory),
        private resolution: Resolution = resolve(Resolution)
    ) {
        super();
    }

    public connectedCallback(): void {
        super.connectedCallback();

        this.closeButton.addEventListener("click", () => this.close());
    }

    public renderOnMap(map: google.maps.Map): google.maps.marker.AdvancedMarkerElement {
        this.marker = this.googleMapsFactory.createMarker({
            map: map,
            position: this.position(),
            title: this.title,
            content: this.makePinIcon()
        });
        return this.marker;
    }

    public position(): google.maps.LatLng {
        return this.googleMapsFactory.createLatLng(this.lat, this.lng);
    }

    public isOpen(): boolean {
        return this.classList.contains("open");
    }

    public toggleOpen(): void {
        this.isOpen() ? this.close() : this.open();
    }

    public open(): void {
        this.classList.add("open");
        if (!this.marker) {
            return;
        }
        this.marker.content = this.makePinIcon(COLOR_HIGHLIGHT);
        this.toggleMapOptions(false);
    }

    public close(): void {
        this.classList.remove("open");
        if (!this.marker) {
            return;
        }
        this.marker.content = this.makePinIcon();
        this.toggleMapOptions(true);
    }

    private toggleMapOptions(switchedOn: boolean): void {
        this.marker.map?.setOptions({mapTypeControl: switchedOn});
        if (switchedOn || this.resolution.upTo(BOOTSTRAP.XS)) {
            this.marker.map?.setOptions({zoomControl: switchedOn, fullscreenControl: switchedOn, streetViewControl: switchedOn});
        }
    }

    private getComputedColor(propertyName: string): string {
        return getComputedStyle(this).getPropertyValue(propertyName);
    }

    private makePinIcon(fillColor?: string): Element {
        const width = 30;
        const height = 35;
        const svg = `<svg xmlns="http://www.w3.org/2000/svg" 
                                    width="${width}px" height="${height}px" viewBox="0 0 ${width + 5} ${height + 5}" 
                                    fill="${this.getComputedColor(fillColor ?? MARKER_COLOR_PROPS[this.color] ?? COLOR_PRIMARY)}"
                                    fill-opacity="1"
                                    fill-rule="evenodd"
                                    stroke="${this.getComputedColor(COLOR_WHITE)}" 
                                    stroke-width="2"
                                    stroke-opacity="1"
                                 >
                                    <g>
                                        <path d="M18.364 17.364L12 23.728l-6.364-6.364a9 9 0 1 1 12.728 0zM12 13a2 2 0 1 0 0-4 2 2 0 0 0 0 4z" transform="scale(1.5)"></path>
                                    </g>
                                 </svg>`;
        return elementFrom(svg);
    }
}